// src/api/notifications/communicationApi.ts
import { ENV } from '../../config/runtimeEnv';
import axios from 'axios';
import type { SendCommunicationRequest, SendCommunicationResponse } from '../../types/communications/CommunicationTypes';
import  { CHANNEL_LABELS } from '../../types/communications/CommunicationTypes';
import { createServiceClient } from '../../lib/http/client';
export const API_BASE_URL = ENV.VITE_NOTIFICATION_BASE_URL;




// Server-side skip reasons the practitioner has to act on differently — a bare "Skipped" hides which.
const SKIP_REASONS: Record<string, string> = {
	skipped_no_email: 'No email address on file',
	skipped_no_phone: 'No phone number on file',
	skipped_no_credits: 'No SMS credits remaining',
	skipped_disabled: "Turned off in the patient's notification preferences",
};


/**
 * Error handling
 */
const describeRequestError = (error: unknown): number | string => {
	if (axios.isAxiosError(error)) {
		return error.response?.status ?? 'no response';
	}
	return 'unknown';
};

const createApiInstance = () => {
	return createServiceClient({
		baseURL: API_BASE_URL,
		headers: {
			'Content-Type': 'application/json',
		},
	});
};

/**
 * Send a communication via the unified preference-based endpoint.
 */
export const sendCommunication = async (
	payload: SendCommunicationRequest
): Promise<SendCommunicationResponse> => {
	try {
		const api = createApiInstance();
		const response = await api.post<SendCommunicationResponse>(
			'/communications/send',
			payload
		);
		return response.data;
	} catch (error) {
		console.error('Error sending communication:', describeRequestError(error));
		throw error;
	}
};


/**
 * Get a human-readable summary of channel dispatch results.
 */
export const getChannelSummary = (channels: Record<string, string>): string => {
	const parts: string[] = [];
	for (const [channel, status] of Object.entries(channels)) {
		const label = CHANNEL_LABELS[channel] ?? channel.charAt(0).toUpperCase() + channel.slice(1);
		if (status === 'sent') {
			parts.push(`${label}: Sent`);
		} else if (status.startsWith('skipped')) {
			parts.push(`${label}: ${SKIP_REASONS[status] ?? 'Skipped'}`);
		} else if (status === 'failed') {
			parts.push(`${label}: Failed`);
		}
	}
	return parts.join(', ');
};

/**
 * Channel counts alongside the summary, so a caller can tell a full send from a partial or empty one.
 */
export const summariseChannelOutcome = (channels: Record<string, string>) => ({
	sent: Object.values(channels).filter((s) => s === 'sent').length,
	total: Object.keys(channels).length,
	summary: getChannelSummary(channels),
});
